import {
	Alert,
	Box,
	Button,
	Icons,
	Inline,
	Input,
	SelectInput,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	DatePickerField,
	SelectInputField,
	TextInputField,
} from "@sembark-travel/ui-form"
import type {
	TAutomationRuleGroupOperator,
	TAutomationSource,
	TAutomationSourceField,
} from "./store"
import {
	ALL_RULE_OPERATORS,
	createRuleGroupFormValue,
	createRuleRowFormValue,
	fieldTypeLabel,
	fieldValueHint,
	formatFieldOptionLabel,
	formatRuleReferenceLabel,
	isKeywordOperator,
	isRuleRowNode,
	operatorLabel,
	operatorsForField,
	ruleRequiresValue,
	stepReferenceFieldOptions,
	stepReferenceHint,
	summarizeRuleGroup,
	summarizeRules,
	type TAutomationDefinitionStepFormValue,
	type TAutomationRuleGroupFormValue,
	type TAutomationRuleNodeFormValue,
	type TAutomationRuleRowFormValue,
} from "./definition-form"

/**
 * The nested AND/OR rule editor, shared by the advanced definition builder and
 * the template form. Its helpers live in `./definition-form` so this module can
 * export a component and nothing else — see the note there.
 */
export function RuleGroupEditor({
	group,
	source,
	fields,
	availableSteps = [],
	allowStepReferences = false,
	onChange,
	prefix,
	depth = 0,
	allowRemove = false,
	allowEmpty = false,
	onRemove,
}: {
	group: TAutomationRuleGroupFormValue
	source?: TAutomationSource | null
	fields: Array<TAutomationSourceField>
	availableSteps?: Array<TAutomationDefinitionStepFormValue>
	allowStepReferences?: boolean
	onChange: (group: TAutomationRuleGroupFormValue) => void
	prefix: string
	depth?: number
	allowRemove?: boolean
	allowEmpty?: boolean
	onRemove?: () => void
}) {
	const replaceNode = (
		nodeId: string,
		updater: (
			node: TAutomationRuleNodeFormValue
		) => TAutomationRuleNodeFormValue
	) => {
		onChange({
			...group,
			rules: group.rules.map((node) =>
				node.id === nodeId ? updater(node) : node
			),
		})
	}

	return (
		<Box
			borderWidth="1"
			rounded="md"
			padding="3"
			backgroundColor={depth === 0 ? undefined : "subtle"}
		>
			<Stack gap="3">
				<Inline
					justifyContent="between"
					alignItems="center"
					gap="2"
					flexWrap="wrap"
				>
					<Stack gap="1">
						<Text fontWeight="semibold">
							{depth === 0 ? "Rule Group" : "Nested Group"}
						</Text>
						<Text color="muted" fontSize="sm">
							{summarizeRuleGroup(group)}
						</Text>
					</Stack>
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						<SelectInputField
							name={`${prefix}.operator`}
							label=""
							onChange={(event) =>
								onChange({
									...group,
									operator: event.currentTarget
										.value as TAutomationRuleGroupOperator,
								})
							}
						>
							<option value="and">Match all rules</option>
							<option value="or">Match any rule</option>
						</SelectInputField>
						{allowRemove && onRemove ? (
							<Button type="button" level="tertiary" onClick={onRemove}>
								<Icons.Trash />
							</Button>
						) : null}
					</Inline>
				</Inline>
				<Stack gap="3">
					{group.rules.map((node, index) =>
						isRuleRowNode(node) ? (
							<Box key={node.id} borderWidth="1" rounded="md">
								<Stack>
									<Inline
										justifyContent="between"
										alignItems="center"
										gap="2"
										bgColor="subtle"
										paddingX="4"
										paddingY="1"
										roundedTop="md"
										borderBottomWidth="1"
									>
										<Text fontWeight="semibold">Rule {index + 1}</Text>
										<Button
											type="button"
											level="tertiary"
											size="sm"
											disabled={!allowEmpty && group.rules.length === 1}
											onClick={() =>
												onChange({
													...group,
													rules: group.rules.filter(
														(item) => item.id !== node.id
													),
												})
											}
										>
											<Icons.Trash />
										</Button>
									</Inline>
									{(() => {
										const selectedField =
											node.field_source === "source"
												? fields.find((item) => item.key === node.field_path)
												: undefined
										// A wildcard path resolves to no declared field, so the
										// picker cannot narrow by type — offer every operator and
										// let the author decide what the value means.
										const isWildcardField =
											node.field_source === "source" &&
											!selectedField &&
											!!source?.wildcard_field &&
											(node.field_path || "").startsWith(
												source.wildcard_field.prefix
											)
										const operators = isWildcardField
											? ALL_RULE_OPERATORS
											: operatorsForField(selectedField)
										const selectedStep = availableSteps.find(
											(stepItem) => stepItem.key === node.step_key
										)
										const stepFieldOptions = node.step_scope
											? stepReferenceFieldOptions(
													source,
													selectedStep,
													node.step_scope
												)
											: []
										return (
											<Stack gap="4" paddingY="2" paddingX="4">
												{allowStepReferences ? (
													<Box>
														<SelectInputField
															name={`${prefix}.rules[${index}].field_source`}
															label="Rule Source"
															onChange={(event) =>
																replaceNode(node.id, (currentNode) =>
																	isRuleRowNode(currentNode)
																		? {
																				...currentNode,
																				field_source: event.currentTarget
																					.value as TAutomationRuleRowFormValue["field_source"],
																				field_path: "",
																				step_key: "",
																				step_scope: "status",
																				show_advanced_step_scope: false,
																				step_field_path: "",
																				show_advanced_step_path: false,
																				value: "",
																			}
																		: currentNode
																)
															}
														>
															<option value="source">Source field</option>
															{allowStepReferences ? (
																<option value="step">
																	Previous step output
																</option>
															) : null}
														</SelectInputField>
													</Box>
												) : null}
												<Inline gap="4" flexWrap="wrap">
													<Stack gap="1">
														{node.field_source === "source" ? (
															(() => {
																const wildcard = source?.wildcard_field
																const fieldPath = node.field_path || ""
																// The bare prefix counts as custom too: it is what
																// picking the wildcard option leaves behind, and
																// the author still has the key to type.
																const isCustom = isWildcardField
																const fieldSelectId = `${prefix}.rules[${index}].field_path`
																return (
																	<Stack gap="2">
																		<Stack gap="1">
																			<label htmlFor={fieldSelectId}>
																				Field
																			</label>
																			<SelectInput
																				id={fieldSelectId}
																				value={
																					isCustom ? "__wildcard__" : fieldPath
																				}
																				onChange={(event) => {
																					const nextValue =
																						event.currentTarget.value
																					const nextFieldPath =
																						nextValue === "__wildcard__"
																							? wildcard?.prefix || ""
																							: nextValue
																					replaceNode(node.id, (currentNode) =>
																						isRuleRowNode(currentNode)
																							? {
																									...currentNode,
																									field_path: nextFieldPath,
																									operator:
																										operatorsForField(
																											fields.find(
																												(fieldItem) =>
																													fieldItem.key ===
																													nextFieldPath
																											)
																										)[0] || "equals",
																									value: "",
																								}
																							: currentNode
																					)
																				}}
																			>
																				<option value="">Select a field</option>
																				{fields.map((fieldOption) => (
																					<option
																						key={fieldOption.key}
																						value={fieldOption.key}
																					>
																						{formatFieldOptionLabel(
																							fieldOption
																						)}
																					</option>
																				))}
																				{wildcard ? (
																					<option value="__wildcard__">
																						{wildcard.label}
																					</option>
																				) : null}
																			</SelectInput>
																		</Stack>
																		{isCustom && wildcard ? (
																			<Stack gap="1">
																				<label
																					htmlFor={`${fieldSelectId}__key`}
																				>
																					{`Custom field key (after ${wildcard.prefix})`}
																				</label>
																				<Input
																					id={`${fieldSelectId}__key`}
																					type="text"
																					placeholder="e.g. lead_score"
																					value={fieldPath.slice(
																						wildcard.prefix.length
																					)}
																					onChange={(event) => {
																						const suffix =
																							event.currentTarget.value
																						replaceNode(
																							node.id,
																							(currentNode) =>
																								isRuleRowNode(currentNode)
																									? {
																											...currentNode,
																											field_path:
																												wildcard.prefix +
																												suffix,
																										}
																									: currentNode
																						)
																					}}
																				/>
																				{wildcard.description ? (
																					<Text fontSize="sm" color="muted">
																						{wildcard.description}
																					</Text>
																				) : null}
																			</Stack>
																		) : null}
																	</Stack>
																)
															})()
														) : (
															<SelectInputField
																name={`${prefix}.rules[${index}].step_key`}
																label="Step"
																onChange={(event) => {
																	const nextStepKey = event.currentTarget.value
																	replaceNode(node.id, (currentNode) =>
																		isRuleRowNode(currentNode)
																			? {
																					...currentNode,
																					step_key: nextStepKey,
																					step_scope: "status",
																					show_advanced_step_scope: false,
																					step_field_path: "",
																					show_advanced_step_path: false,
																					value: "",
																				}
																			: currentNode
																	)
																}}
															>
																<option value="">Select a previous step</option>
																{availableSteps.map((stepOption) => (
																	<option
																		key={stepOption.id}
																		value={stepOption.key}
																	>
																		{stepOption.key}
																	</option>
																))}
															</SelectInputField>
														)}
														<Text color="muted" fontSize="sm">
															{node.field_source === "source"
																? isWildcardField
																	? "Type: not declared"
																	: `Type: ${fieldTypeLabel(selectedField)}`
																: selectedStep
																	? `Step ${selectedStep.key}`
																	: "Choose a prior step to reference its status or outputs."}
														</Text>
													</Stack>
													{node.field_source === "step" ? (
														<Stack gap="2">
															<SelectInputField
																name={`${prefix}.rules[${index}].step_scope`}
																label="Step Field"
																onChange={(event) =>
																	replaceNode(node.id, (currentNode) =>
																		isRuleRowNode(currentNode)
																			? {
																					...currentNode,
																					step_scope: event.currentTarget
																						.value as TAutomationRuleRowFormValue["step_scope"],
																					step_field_path: "",
																					show_advanced_step_path: false,
																					value: "",
																				}
																			: currentNode
																	)
																}
															>
																<option value="status">Status</option>
																<option value="output">Output</option>
																{node.show_advanced_step_scope ||
																node.step_scope === "meta" ? (
																	<option value="meta">Meta</option>
																) : null}
															</SelectInputField>
															{!node.show_advanced_step_scope ? (
																<Button
																	type="button"
																	level="tertiary"
																	onClick={() =>
																		replaceNode(node.id, (currentNode) =>
																			isRuleRowNode(currentNode)
																				? {
																						...currentNode,
																						show_advanced_step_scope: true,
																					}
																				: currentNode
																		)
																	}
																>
																	Show Advanced
																</Button>
															) : (
																<Button
																	type="button"
																	level="tertiary"
																	onClick={() =>
																		replaceNode(node.id, (currentNode) =>
																			isRuleRowNode(currentNode)
																				? {
																						...currentNode,
																						show_advanced_step_scope: false,
																						step_scope:
																							currentNode.step_scope === "meta"
																								? "output"
																								: currentNode.step_scope,
																						step_field_path:
																							currentNode.step_scope === "meta"
																								? ""
																								: currentNode.step_field_path,
																						value:
																							currentNode.step_scope === "meta"
																								? ""
																								: currentNode.value,
																					}
																				: currentNode
																		)
																	}
																>
																	Hide Advanced
																</Button>
															)}
															{node.step_scope !== "status" &&
															stepFieldOptions.length &&
															!node.show_advanced_step_path ? (
																<Stack gap="1">
																	<label
																		htmlFor={`${prefix}.rules[${index}].step_field_path`}
																	>
																		Known Field
																	</label>
																	<SelectInput
																		id={`${prefix}.rules[${index}].step_field_path`}
																		value={
																			stepFieldOptions.some(
																				(option) =>
																					option.path === node.step_field_path
																			)
																				? node.step_field_path || ""
																				: ""
																		}
																		onChange={(event) =>
																			replaceNode(node.id, (currentNode) =>
																				isRuleRowNode(currentNode)
																					? {
																							...currentNode,
																							step_field_path:
																								event.currentTarget.value,
																						}
																					: currentNode
																			)
																		}
																	>
																		<option value="">
																			Select a known field
																		</option>
																		{node.step_field_path &&
																		!stepFieldOptions.some(
																			(option) =>
																				option.path === node.step_field_path
																		) ? (
																			<option value={node.step_field_path}>
																				{`Current field: ${node.step_field_path}`}
																			</option>
																		) : null}
																		{stepFieldOptions.map((option) => (
																			<option
																				key={option.path}
																				value={option.path}
																			>
																				{option.label}
																			</option>
																		))}
																	</SelectInput>
																</Stack>
															) : null}
														</Stack>
													) : null}
													{node.field_source === "step" &&
													node.step_scope !== "status" ? (
														<Stack gap="2">
															{!node.show_advanced_step_path ? (
																<Button
																	type="button"
																	level="tertiary"
																	onClick={() =>
																		replaceNode(node.id, (currentNode) =>
																			isRuleRowNode(currentNode)
																				? {
																						...currentNode,
																						show_advanced_step_path: true,
																					}
																				: currentNode
																		)
																	}
																>
																	Advanced
																</Button>
															) : (
																<>
																	<TextInputField
																		name={`${prefix}.rules[${index}].step_field_path`}
																		label="Custom Path"
																		placeholder="deliveries.0.wa_thread_id"
																	/>
																	<Button
																		type="button"
																		level="tertiary"
																		onClick={() =>
																			replaceNode(node.id, (currentNode) =>
																				isRuleRowNode(currentNode)
																					? {
																							...currentNode,
																							show_advanced_step_path: false,
																						}
																					: currentNode
																			)
																		}
																	>
																		Hide Advanced
																	</Button>
																</>
															)}
														</Stack>
													) : null}
													<SelectInputField
														name={`${prefix}.rules[${index}].operator`}
														label="Operator"
													>
														{operators.map((operator) => (
															<option key={operator} value={operator}>
																{operatorLabel(operator)}
															</option>
														))}
													</SelectInputField>
													<Box>
														{node.field_source === "step" &&
														node.step_scope === "status" ? (
															<SelectInputField
																name={`${prefix}.rules[${index}].value`}
																label="Value"
																disabled={!ruleRequiresValue(node.operator)}
															>
																<option value="">Select status</option>
																{[
																	"pending",
																	"running",
																	"completed",
																	"failed",
																	"cancelled",
																].map((status) => (
																	<option key={status} value={status}>
																		{status}
																	</option>
																))}
															</SelectInputField>
														) : selectedField?.options?.length ? (
															<SelectInputField
																name={`${prefix}.rules[${index}].value`}
																label="Value"
																disabled={!ruleRequiresValue(node.operator)}
															>
																<option value="">Select value</option>
																{selectedField.options.map((option) => (
																	<option
																		key={String(option.value)}
																		value={String(option.value)}
																	>
																		{option.label}
																	</option>
																))}
															</SelectInputField>
														) : selectedField?.type === "boolean" ? (
															<SelectInputField
																name={`${prefix}.rules[${index}].value`}
																label="Value"
																disabled={!ruleRequiresValue(node.operator)}
															>
																<option value="">Select value</option>
																<option value="true">True</option>
																<option value="false">False</option>
															</SelectInputField>
														) : ["date", "datetime"].includes(
																selectedField?.type || ""
														  ) ? (
															<DatePickerField
																name={`${prefix}.rules[${index}].value`}
																label="Value"
																disabled={!ruleRequiresValue(node.operator)}
																dateFormat={
																	selectedField?.type === "datetime"
																		? "DD MMM, YYYY hh:mm A"
																		: "DD MMM, YYYY"
																}
															/>
														) : selectedField?.type === "number" ? (
															<TextInputField
																name={`${prefix}.rules[${index}].value`}
																label="Value"
																type="number"
																disabled={!ruleRequiresValue(node.operator)}
																placeholder="Enter number"
															/>
														) : (
															<TextInputField
																name={`${prefix}.rules[${index}].value`}
																label="Value"
																disabled={!ruleRequiresValue(node.operator)}
																placeholder={
																	isKeywordOperator(node.operator)
																		? "hi, hello, good morning"
																		: node.operator === "in"
																			? "Value 1, Value 2"
																			: "Enter value"
																}
															/>
														)}
													</Box>
												</Inline>
												<Text color="muted" fontSize="sm">
													{node.field_source === "step"
														? node.step_scope
															? stepReferenceHint(
																	source,
																	selectedStep,
																	node.step_scope
																)
															: ""
														: fieldValueHint(selectedField, isWildcardField)}
												</Text>
											</Stack>
										)
									})()}
									<Box
										paddingX="4"
										paddingY="1"
										roundedBottom="md"
										bgColor="subtle"
										borderTopWidth="1"
									>
										<Text color="muted" fontSize="sm">
											Field: {formatRuleReferenceLabel(source, node)}
										</Text>
									</Box>
								</Stack>
							</Box>
						) : (
							<RuleGroupEditor
								key={node.id}
								group={node}
								source={source}
								fields={fields}
								availableSteps={availableSteps}
								allowStepReferences={allowStepReferences}
								prefix={`${prefix}.rules[${index}]`}
								depth={depth + 1}
								allowRemove
								onRemove={() =>
									onChange({
										...group,
										rules: group.rules.filter((item) => item.id !== node.id),
									})
								}
								onChange={(nextGroup) => replaceNode(node.id, () => nextGroup)}
							/>
						)
					)}
				</Stack>

				<Inline gap="2" flexWrap="wrap">
					<Button
						type="button"
						level="secondary"
						onClick={() =>
							onChange({
								...group,
								rules: [...group.rules, createRuleRowFormValue()],
							})
						}
					>
						<Icons.Plus /> Add Rule
					</Button>
					<Button
						type="button"
						level="tertiary"
						onClick={() =>
							onChange({
								...group,
								rules: [...group.rules, createRuleGroupFormValue()],
							})
						}
					>
						<Icons.RectangleStack /> Add Group
					</Button>
				</Inline>
				<Alert status="info" hideIcon>
					<Text fontSize="sm">{summarizeRules(source, group)}</Text>
				</Alert>
			</Stack>
		</Box>
	)
}
